import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { CompanyMemberDocument } from '@/types'

interface RateSkillsFormValues {
  hourlyRate: string
  productionSkillsRaw: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  member: CompanyMemberDocument & { id: string }
  onSave: (id: string, hourlyRate: number | undefined, productionSkills: string[]) => Promise<void>
}

export function PersonnelRateSkillsDialog({ open, onOpenChange, member, onSave }: Props) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { register, handleSubmit, reset } = useForm<RateSkillsFormValues>()

  useEffect(() => {
    if (open) {
      reset({
        hourlyRate: member.hourlyRate != null ? String(member.hourlyRate) : '',
        productionSkillsRaw: (member.productionSkills ?? []).join(', '),
      })
      setError(null)
    }
  }, [open, member, reset])

  async function onSubmit(data: RateSkillsFormValues) {
    setSaving(true)
    setError(null)
    try {
      const rate = data.hourlyRate.trim() !== '' ? Number(data.hourlyRate) : undefined
      const skills = data.productionSkillsRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      await onSave(member.id, rate, skills)
      onOpenChange(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Rate & Skills — {member.name}</DialogTitle>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="rate">Hourly Rate ($/hr)</Label>
            <Input
              id="rate"
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              {...register('hourlyRate')}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="skills">Production Skills</Label>
            <Input
              id="skills"
              placeholder="Camera Operator, Editor, Director"
              {...register('productionSkillsRaw')}
            />
            <p className="text-xs text-muted-foreground">Comma-separated list of skills</p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
